import { MedChronExportOptionsFields } from "@/components/MedicalChronology/MedChronExportOptionsFields";
import { Loading } from "@/components/ui";
import type { MedChronExportUiState } from "@/services/userPreferencesService.types";

interface PreferencesTabViewProps {
  formState: MedChronExportUiState | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isSaving: boolean;
  isDirty: boolean;
  onFieldChange: (patch: Partial<MedChronExportUiState>) => void;
  onReset: () => void;
  onSave: () => void;
}

export const PreferencesTabView = ({
  formState,
  isLoading,
  isError,
  errorMessage,
  isSaving,
  isDirty,
  onFieldChange,
  onReset,
  onSave,
}: PreferencesTabViewProps) => {
  if (isError) {
    return (
      <p
        role="alert"
        className="rounded-lg bg-destructive-subtle p-4 text-sm font-medium text-destructive-text"
      >
        {errorMessage || "Failed to load export preferences."}
      </p>
    );
  }

  if (isLoading || !formState) {
    return (
      <div className="py-10">
        <Loading fullScreen={false} size={32} text="Loading preferences..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-2 text-sm text-muted-foreground">
        Defaults used for your MedChron exports. Changes apply the next time you export.
      </p>

      <MedChronExportOptionsFields formState={formState} onFieldChange={onFieldChange} />

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onReset}
          disabled={!isDirty || isSaving}
          className="flex-1 rounded-2xl border border-input bg-card py-3 text-sm font-bold text-muted-foreground transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || isSaving}
          className="flex-1 rounded-2xl bg-primary py-3 text-sm font-bold text-white shadow-md transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};
